/**
 * BackendWidgetRenderer - Renders a widget from backend format (BackendWidget)
 *
 * Converts backend widget format to V2 config, fetches data via useWidgetData,
 * and renders using WidgetRenderer.
 */

import React, { useMemo } from "react";
import type { BackendWidget } from "@/models/widget-v2";
import type { FiltersContext } from "@/models/filter";
import { WidgetRenderer } from "./WidgetRenderer";
import type {
  WidgetConfig,
  DataConfig,
  VisualConfig,
  BucketDataConfig,
  CategoryDataConfig,
  ChartVisualConfig,
  DocumentsVisualConfig,
  GraphVisualConfig,
} from "@/models/widget-v2";
import {
  fromBackendWidgetType,
  createDefaultWidgetConfig,
  getBackendWidgetType,
  WIDGET_TYPES,
} from "@/models/widget-v2";
import { useWidgetData } from "@/hooks/use-widget-data";

// ============================================================================
// Types
// ============================================================================

interface BackendWidgetRendererProps {
  widget: BackendWidget;
  filtersCtx?: FiltersContext;
}

// ============================================================================
// Conversion Functions
// ============================================================================

/**
 * Converts a BackendWidget to V2 WidgetConfig
 */
function convertBackendWidgetToConfig(widget: BackendWidget): WidgetConfig {
  const widgetType = fromBackendWidgetType(
    getBackendWidgetType(widget, "line_widget")
  );
  const defaultConfig = createDefaultWidgetConfig(
    widgetType,
    widget.id || `widget-${Date.now()}`
  );

  const dataConfig = extractDataConfig(widget, widgetType);
  const visualConfig = extractVisualConfig(widget, widgetType);

  return {
    id: widget.id || `widget-${Date.now()}`,
    type: widgetType,
    name: widget.metadata?.name || defaultConfig.name,
    data: dataConfig || defaultConfig.data,
    visual: visualConfig || defaultConfig.visual,
  };
}

/**
 * Extracts data configuration from backend widget
 */
function extractDataConfig(
  widget: BackendWidget,
  widgetType: string
): DataConfig | null {
  // BucketMetric widgets (line, bar, area, table)
  const bucketTypes = [
    WIDGET_TYPES.LINE,
    WIDGET_TYPES.BAR,
    WIDGET_TYPES.AREA,
    WIDGET_TYPES.TABLE,
  ];
  if (bucketTypes.includes(widgetType as typeof WIDGET_TYPES.LINE)) {
    if (widget.bucket_field || widget.bucket_type || widget.metrics) {
      return {
        kind: "bucket",
        bucketField: widget.bucket_field || "date$date",
        bucketType:
          (widget.bucket_type as BucketDataConfig["bucketType"]) ||
          "date_histogram",
        metrics: widget.metrics?.map((m) => ({
          name: m.name || "count",
          type: m.type || "count",
          field: m.field || null,
        })) || [{ name: "count", type: "count", field: null }],
        size: widget.size || 10,
        dateInterval:
          (widget.date_interval as BucketDataConfig["dateInterval"]) || "month",
        numericInterval: widget.numeric_interval,
        ...(widget.split_by && {
          splitBy: widget.split_by,
          splitBySize: widget.split_by_size ?? 20,
        }),
      };
    }
  }

  // CategoryMetric widgets (pie, wordcloud)
  const categoryTypes = [WIDGET_TYPES.PIE, WIDGET_TYPES.WORDCLOUD];
  if (categoryTypes.includes(widgetType as typeof WIDGET_TYPES.PIE)) {
    if (widget.field || widget.metric) {
      return {
        kind: "category",
        field: widget.field || "text$type",
        metric: widget.metric
          ? {
              name: widget.metric.name || "count",
              type: widget.metric.type || "count",
              field: widget.metric.field || null,
            }
          : { name: "count", type: "count", field: null },
        size: widget.size || 10,
        minDocCount: widget.min_doc_count || 1,
        showMissing: widget.show_missing || false,
        missingLabel: widget.missing_label || "Missing",
      };
    }
  }

  // GlobalMetric widgets (kpi, gauge)
  const metricTypes = [WIDGET_TYPES.KPI, WIDGET_TYPES.GAUGE];
  if (metricTypes.includes(widgetType as typeof WIDGET_TYPES.KPI)) {
    return {
      kind: "global",
      metric: widget.metric
        ? {
            name: widget.metric.name || "count",
            type: widget.metric.type || "count",
            field: widget.metric.field || null,
          }
        : { name: "count", type: "count", field: null },
    };
  }

  // Documents/Graph widgets
  const docTypes = [WIDGET_TYPES.DOCUMENTS, WIDGET_TYPES.GRAPH];
  if (docTypes.includes(widgetType as typeof WIDGET_TYPES.DOCUMENTS)) {
    return {
      kind: "documents",
      size: widget.size || 20,
    };
  }

  // Text widget
  if (widgetType === WIDGET_TYPES.TEXT) {
    return { kind: "static" };
  }

  return null;
}

/**
 * Extracts visual configuration from backend widget.
 * Prioritizes stored visual config from metadata.visual when available.
 */
function extractVisualConfig(
  widget: BackendWidget,
  widgetType: string
): VisualConfig {
  const name = widget.metadata?.name || widget.name || "Widget";

  // Restore persisted visual config from metadata
  const storedVisual = widget.metadata?.visual as Record<string, unknown> | undefined;
  if (storedVisual && Object.keys(storedVisual).length > 0) {
    return { ...storedVisual, title: (storedVisual.title as string) || name } as VisualConfig;
  }

  if (widgetType === WIDGET_TYPES.DOCUMENTS) {
    return {
      title: name,
      defaultView: "card",
    } as DocumentsVisualConfig;
  }

  if (widgetType === WIDGET_TYPES.GRAPH) {
    return {
      title: name,
      showLegend: true,
      nodeSize: 15,
      linkDistance: 30,
      chargeStrength: -200,
      showLabels: true,
      showTooltips: true,
    } as GraphVisualConfig;
  }

  return {
    title: name,
    showLegend: true,
    legendPosition: "top",
    showGrid: true,
    animation: true,
    tooltips: true,
  } as ChartVisualConfig;
}

// ============================================================================
// Component
// ============================================================================

export const BackendWidgetRenderer: React.FC<BackendWidgetRendererProps> = ({
  widget,
  filtersCtx,
}) => {
  // Convert backend widget to V2 config
  const config = useMemo(() => convertBackendWidgetToConfig(widget), [widget]);

  // Fetch widget data
  const { data, isLoading, isError } = useWidgetData({
    config,
    filters: filtersCtx?.filtersData,
    enabled: config.data.kind !== "static",
  });

  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center text-content-tertiary">
        Chargement...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="h-full w-full flex items-center justify-center text-error">
        Erreur de chargement
      </div>
    );
  }

  return <WidgetRenderer config={config} data={data} />;
};

export default BackendWidgetRenderer;
